// ENG-6600: pure, unit-testable computation of the per-channel config hash
// the manager uses to decide whether to re-render an agent's channel
// `.mcp.json` entry (slack / telegram / msteams / direct-chat) on each poll.
//
// The hash is intentionally an "inputs + version" fingerprint. The
// `knownChannelConfigHashes` cache skips the (expensive) credential rewrite
// whenever the hash is unchanged, so the steady state is zero churn. The hash
// MUST therefore change whenever the rendered entry would change, including
// when the change is code-only (a new env var the adapter emits), not a
// config change. That code-only class is what the manual `writeVersion` knob
// historically tracked (v4–v9), and what folding the build-time agt CLI
// version (`cliVersion`) into the hash now covers structurally: every release
// that changes channel rendering auto-publishes a new CLI (ENG-5795), so the
// hash moves and the entry is re-rendered exactly once per self-update.
//
// See the call site in `manager-worker.ts` for why this does NOT cause restart
// storms (the `.mcp.json` drift watcher absorbs a value-only re-render without
// bouncing the session; an identical re-render is a content-hash no-op).

import { createHash } from 'node:crypto';

import { canonicalJson } from './canonical-json.js';

/**
 * The manual write-version knob. Bumping it forces a one-shot rewrite of every
 * agent's channel entries on the next poll. Used historically (v4-v9) when a
 * code-only rendering change had to reach already-deployed agents and there was
 * no release to ride. The `cliVersion` fold (ENG-6600) now subsumes this for
 * ordinary code-only changes; the constant remains as an explicit-intent
 * override (e.g. forcing a rewrite without cutting a release).
 */
export const CHANNEL_WRITE_VERSION = 9;

export interface ChannelConfigHashInput {
  /** The build-time agt CLI version (`agtCliVersion`); 'dev' under tsx. */
  cliVersion: string;
  /** The raw channel config object from /host/refresh. */
  config: unknown;
  /** Team settings folded into the hash (timezone, etc.). */
  team: unknown;
  /** Peer roster folded into the hash. */
  peers: unknown;
  /** Effective session mode ('persistent' | 'oneshot' | null). */
  sessionMode: unknown;
  /** Effective sender policy (or null/undefined). */
  senderPolicy: unknown;
}

/**
 * Compute the sha256 fingerprint that gates channel-credential rewrites.
 *
 * Deterministic: identical input (same `cliVersion` included) yields the same
 * digest, so an unchanged channel is never rewritten poll-to-poll. A different
 * `cliVersion` yields a different digest, so a CLI self-update re-renders the
 * entry once: that is the propagation guarantee for code-only env/args
 * additions (ENG-6600).
 */
export function computeChannelConfigHash(input: ChannelConfigHashInput): string {
  return createHash('sha256')
    .update(
      canonicalJson({
        writeVersion: CHANNEL_WRITE_VERSION,
        cliVersion: input.cliVersion,
        config: input.config,
        team: input.team,
        peers: input.peers,
        sessionMode: input.sessionMode ?? null,
        senderPolicy: input.senderPolicy,
      }),
    )
    .digest('hex');
}
